import type { WebEditorMediaAssetUpdateInput } from '../../media-library.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { WebEditorMediaAsset, WebEditorMediaKind } from '../../media-library.js';


export interface WebEditorMediaCatalog {
  listMediaAssets(): Promise<WebEditorMediaAsset[]>;
  uploadAsset(file: File): Promise<{ url: string; target?: string; asset?: WebEditorMediaAsset }>;
  updateMediaAsset(id: string, update: WebEditorMediaAssetUpdateInput): Promise<WebEditorMediaAsset>;
  deleteMediaAsset(id: string, expectedRevision: number): Promise<unknown>;
}
export interface EditorMediaLibraryProps { onInspect: () => void; }
export const createMediaLibrary = (adapter: WebEditorMediaCatalog) => {
type MediaKindFilter = 'all' | WebEditorMediaKind;
type MediaUiReason = 'selection' | 'metadata' | 'refresh';

interface MediaUiSnapshot {
  selected: WebEditorMediaAsset | null;
  folders: string[];
}

class EditorMediaUiState {
  private selected: WebEditorMediaAsset | null = null;
  private folders: string[] = [];
  private readonly listeners = new Set<(snapshot: MediaUiSnapshot, reason: MediaUiReason) => void>();

  snapshot(): MediaUiSnapshot {
    return { selected: this.selected, folders: this.folders };
  }

  subscribe(listener: (snapshot: MediaUiSnapshot, reason: MediaUiReason) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  select(asset: WebEditorMediaAsset | null) {
    this.selected = asset;
    this.emit('selection');
  }

  update(asset: WebEditorMediaAsset) {
    if (this.selected?.id === asset.id) this.selected = asset;
    this.emit('metadata');
  }

  remove(id: string) {
    if (this.selected?.id === id) this.selected = null;
    this.emit('metadata');
  }

  reconcile(assets: WebEditorMediaAsset[]) {
    this.folders = [...new Set(assets.map(asset => asset.folder).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right, 'de'));
    if (this.selected) this.selected = assets.find(asset => asset.id === this.selected?.id) ?? null;
    this.emit('refresh');
  }

  private emit(reason: MediaUiReason) {
    const snapshot = this.snapshot();
    this.listeners.forEach(listener => listener(snapshot, reason));
  }
}

const mediaUiState = new EditorMediaUiState();

const kindLabels: Record<WebEditorMediaKind, string> = {
  image: 'Bild',
  video: 'Video',
  caption: 'Untertitel',
};

const kindIcons: Record<WebEditorMediaKind, string> = {
  image: 'icon-[material-symbols--image]',
  video: 'icon-[material-symbols--movie]',
  caption: 'icon-[material-symbols--closed-caption]',
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
};

const errorMessage = (error: unknown, fallback: string) => error instanceof Error && error.message ? error.message : fallback;

const renderMediaPreview = (asset: WebEditorMediaAsset, detailed = false) => asset.kind === 'image'
  ? html`<img src=${asset.url} alt="" loading="lazy" decoding="async" />`
  : asset.kind === 'video' && detailed
    ? html`<video src=${asset.url} controls preload="metadata"></video>`
    : html`<span class="${kindIcons[asset.kind]} vp-editor-media-kind-icon" aria-hidden="true"></span>`;


const EditorMediaLibrary = stateful((props: EditorMediaLibraryProps) => {
  const [assets, setAssets] = useState<WebEditorMediaAsset[]>([], []);
  const [selectedId, setSelectedId] = useState<string | null>(mediaUiState.snapshot().selected?.id ?? null, []);
  const [folder, setFolder] = useState('all', []);
  const [kind, setKind] = useState<MediaKindFilter>('all', []);
  const [query, setQuery] = useState('', []);
  const [isLoading, setIsLoading] = useState(true, []);
  const [isUploading, setIsUploading] = useState(false, []);
  const [isSaving, setIsSaving] = useState(false, []);
  const [error, setError] = useState('', []);
  const [notice, setNotice] = useState('', []);

  const reload = async (announce = false) => {
    setIsLoading(true);
    setError('');
    try {
      const nextAssets = await adapter.listMediaAssets();
      setAssets(nextAssets);
      mediaUiState.reconcile(nextAssets);
      if (announce) setNotice('Medien aktualisiert.');
    } catch (loadError) {
      setError(errorMessage(loadError, 'Medien konnten nicht geladen werden.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    return mediaUiState.subscribe((snapshot, reason) => {
      setSelectedId(snapshot.selected?.id ?? null);
      if (reason === 'metadata') void reload();
    });
  }, []);

  const folders = mediaUiState.snapshot().folders;
  const normalizedQuery = query.trim().toLocaleLowerCase('de');
  const filteredAssets = assets.filter(asset => {
    if (folder === 'root' && asset.folder) return false;
    if (folder !== 'all' && folder !== 'root' && asset.folder !== folder) return false;
    if (kind !== 'all' && asset.kind !== kind) return false;
    return !normalizedQuery || `${asset.displayName} ${asset.originalFilename} ${asset.folder}`.toLocaleLowerCase('de').includes(normalizedQuery);
  });

  const selectAsset = (asset: WebEditorMediaAsset) => {
    mediaUiState.select(asset);
    props.onInspect();
    setError('');
    setNotice('');
  };

  const upload = async (event: Event) => {
    const input = event.currentTarget as HTMLInputElement;
    const files = [...(input.files ?? [])];
    if (!files.length) return;
    setIsUploading(true);
    setError('');
    setNotice('');
    try {
      for (const file of files) {
        const result = await adapter.uploadAsset(file);
        if (result.target !== 'backend') throw new Error('Der Upload konnte nur lokal vorgemerkt werden. Bitte Verbindung prüfen.');
      }
      await reload();
      setNotice(`${files.length} ${files.length === 1 ? 'Medium' : 'Medien'} hochgeladen.`);
    } catch (uploadError) {
      setError(errorMessage(uploadError, 'Upload fehlgeschlagen.'));
    } finally {
      input.value = '';
      setIsUploading(false);
    }
  };

  const moveToFolder = async (assetId: string, nextFolder: string) => {
    const asset = assets.find(entry => entry.id === assetId);
    if (!asset || asset.folder === nextFolder) return;
    setIsSaving(true);
    setError('');
    try {
      const updated = await adapter.updateMediaAsset(asset.id, {
        expectedRevision: asset.revision,
        folder: nextFolder,
      });
      const merged = { ...updated, usageCount: asset.usageCount };
      setAssets(assets.map(entry => entry.id === asset.id ? merged : entry));
      mediaUiState.update(merged);
      setNotice(nextFolder ? `Nach „${nextFolder}“ verschoben.` : 'Aus dem Ordner entfernt.');
    } catch (moveError) {
      setError(errorMessage(moveError, 'Medium konnte nicht verschoben werden.'));
      void reload();
    } finally {
      setIsSaving(false);
    }
  };

  const folderDropHandlers = (targetFolder: string) => ({
    dragover: (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      (event.currentTarget as HTMLElement).classList.add('is-drop-target');
    },
    dragleave: (event: DragEvent) => (event.currentTarget as HTMLElement).classList.remove('is-drop-target'),
    drop: (event: DragEvent) => {
      event.preventDefault();
      (event.currentTarget as HTMLElement).classList.remove('is-drop-target');
      const assetId = event.dataTransfer?.getData('application/x-viva-media-id') ?? '';
      if (assetId) void moveToFolder(assetId, targetFolder);
    },
  });

  return html`
    <section class="vp-editor-media-library" aria-label="Medienverwaltung">
      <header class="vp-editor-media-head">
        <span><strong>Medien</strong><small>${assets.length} Dateien</small></span>
        <span class="vp-editor-media-head-actions">
          <button type="button" title="Medien neu laden" aria-label="Medien neu laden" ?disabled=${isLoading} @click=${() => void reload(true)}>
            <span class="icon-[material-symbols--refresh]"></span>
          </button>
          <label class="vp-editor-media-upload ${isUploading ? 'is-busy' : ''}" title="Bilder oder Videos hochladen">
            <span class="icon-[material-symbols--upload]"></span>
            <span>${isUploading ? 'Lädt …' : 'Upload'}</span>
            <input type="file" multiple accept="image/*,video/*,.vtt,text/vtt" ?disabled=${isUploading} @change=${upload} />
          </label>
        </span>
      </header>

      <label class="vp-editor-media-search">
        <span class="icon-[material-symbols--search]" aria-hidden="true"></span>
        <span class="sr-only">Medien durchsuchen</span>
        <input type="search" placeholder="Medien suchen" .value=${query} @input=${(event: InputEvent) => setQuery((event.target as HTMLInputElement).value)} />
      </label>

      <div class="vp-editor-media-kind-filter" aria-label="Dateityp filtern">
        ${([['all', 'Alle'], ['image', 'Bilder'], ['video', 'Videos']] as const).map(([value, label]) => html`
          <button type="button" class=${kind === value ? 'is-active' : ''} aria-pressed=${String(kind === value)} @click=${() => setKind(value)}>${label}</button>
        `)}
      </div>

      <nav class="vp-editor-media-folders" aria-label="Medienordner">
        <button type="button" class=${folder === 'all' ? 'is-active' : ''} @click=${() => setFolder('all')}>
          <span class="icon-[material-symbols--folder-open]"></span><span>Alle Medien</span><small>${assets.length}</small>
        </button>
        <button type="button" class=${folder === 'root' ? 'is-active' : ''} @click=${() => setFolder('root')} @dragover=${folderDropHandlers('').dragover} @dragleave=${folderDropHandlers('').dragleave} @drop=${folderDropHandlers('').drop}>
          <span class="icon-[material-symbols--folder]"></span><span>Ohne Ordner</span><small>${assets.filter(asset => !asset.folder).length}</small>
        </button>
        ${folders.map(name => html`
          <button type="button" class=${folder === name ? 'is-active' : ''} @click=${() => setFolder(name)} @dragover=${folderDropHandlers(name).dragover} @dragleave=${folderDropHandlers(name).dragleave} @drop=${folderDropHandlers(name).drop}>
            <span class="icon-[material-symbols--folder]"></span><span>${name}</span><small>${assets.filter(asset => asset.folder === name).length}</small>
          </button>
        `)}
      </nav>

      ${error ? html`<p class="vp-editor-media-alert is-error" role="alert">${error}</p>` : ''}
      ${notice ? html`<p class="vp-editor-media-alert is-success" role="status">${notice}</p>` : ''}
      ${isSaving ? html`<span class="vp-editor-media-saving" role="status">Änderung wird gespeichert …</span>` : ''}

      ${isLoading
        ? html`<div class="vp-editor-media-loading" aria-label="Medien werden geladen"><i></i><i></i><i></i><i></i></div>`
        : filteredAssets.length
          ? html`<div class="vp-editor-media-grid">${filteredAssets.map(asset => html`
              <button
                type="button"
                class="vp-editor-media-card ${selectedId === asset.id ? 'is-selected' : ''}"
                aria-pressed=${String(selectedId === asset.id)}
                title=${asset.displayName}
                draggable="true"
                @dragstart=${(event: DragEvent) => {
                  event.dataTransfer?.setData('application/x-viva-media-id', asset.id);
                  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
                  (event.currentTarget as HTMLElement).classList.add('is-dragging');
                }}
                @dragend=${(event: DragEvent) => (event.currentTarget as HTMLElement).classList.remove('is-dragging')}
                @click=${() => selectAsset(asset)}
              >
                <span class="vp-editor-media-thumb">${renderMediaPreview(asset)}</span>
                <span class="vp-editor-media-card-copy"><strong>${asset.displayName}</strong><small>${kindLabels[asset.kind]} · ${formatBytes(asset.size)}</small></span>
                ${asset.usageCount ? html`<span class="vp-editor-media-use" title="${asset.usageCount} Verwendungen">${asset.usageCount}</span>` : ''}
              </button>
            `)}</div>`
          : html`<div class="vp-editor-media-empty">
              <span class="icon-[material-symbols--perm-media]"></span>
              <strong>${assets.length ? 'Keine Treffer' : 'Noch keine Medien'}</strong>
              <small>${assets.length ? 'Filter oder Suche anpassen.' : 'Lade Bilder und Videos hoch, um sie zentral zu verwalten.'}</small>
            </div>`}
    </section>
  `;
});

const EditorMediaInspector = stateful(() => {
  const initial = mediaUiState.snapshot();
  const [snapshot, setSnapshot] = useState<MediaUiSnapshot>(initial, []);
  const [draftName, setDraftName] = useState(initial.selected?.displayName ?? '', []);
  const [draftFolder, setDraftFolder] = useState(initial.selected?.folder ?? '', []);
  const [isSaving, setIsSaving] = useState(false, []);
  const [error, setError] = useState('', []);
  const [notice, setNotice] = useState('', []);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null, []);

  useEffect(() => mediaUiState.subscribe((next, reason) => {
    setSnapshot(next);
    if (reason === 'selection' || reason === 'metadata') {
      setDraftName(next.selected?.displayName ?? '');
      setDraftFolder(next.selected?.folder ?? '');
      setDeleteConfirmationId(null);
      setError('');
      setNotice('');
    }
  }), []);

  const selected = snapshot.selected;
  if (!selected) return html`
    <div class="vp-editor-media-inspector-empty">
      <span class="icon-[material-symbols--perm-media]"></span>
      <strong>Medium auswählen</strong>
      <small>Wähle links ein Bild oder Video aus. Die Details erscheinen dann hier.</small>
    </div>
  `;

  const saveMetadata = async () => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await adapter.updateMediaAsset(selected.id, {
        expectedRevision: selected.revision,
        displayName: draftName,
        folder: draftFolder,
      });
      mediaUiState.update({ ...updated, usageCount: selected.usageCount });
      setNotice('Medium aktualisiert.');
    } catch (saveError) {
      setError(errorMessage(saveError, 'Medium konnte nicht geändert werden.'));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAsset = async () => {
    if (selected.usageCount) return;
    if (deleteConfirmationId !== selected.id) {
      setDeleteConfirmationId(selected.id);
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await adapter.deleteMediaAsset(selected.id, selected.revision);
      mediaUiState.remove(selected.id);
    } catch (deleteError) {
      setError(errorMessage(deleteError, 'Medium konnte nicht gelöscht werden.'));
    } finally {
      setIsSaving(false);
    }
  };

  return html`
    <section class="vp-editor-media-inspector" aria-label="Medium bearbeiten">
      <header>
        <span><strong>Details</strong><small>${kindLabels[selected.kind]}</small></span>
        <button type="button" title="Details schließen" aria-label="Details schließen" @click=${() => mediaUiState.select(null)}>
          <span class="icon-[material-symbols--close]"></span>
        </button>
      </header>
      <div class="vp-editor-media-preview">${renderMediaPreview(selected, true)}</div>
      ${error ? html`<p class="vp-editor-media-alert is-error" role="alert">${error}</p>` : ''}
      ${notice ? html`<p class="vp-editor-media-alert is-success" role="status">${notice}</p>` : ''}
      <label>Name<input maxlength="120" .value=${draftName} @input=${(event: InputEvent) => setDraftName((event.target as HTMLInputElement).value)} /></label>
      <label>
        Ordner (neu oder vorhanden)
        <input list="vp-editor-media-folder-options" maxlength="195" placeholder="z. B. Kampagne / Startseite" .value=${draftFolder} @input=${(event: InputEvent) => setDraftFolder((event.target as HTMLInputElement).value)} />
      </label>
      <datalist id="vp-editor-media-folder-options">${snapshot.folders.map(name => html`<option value=${name}></option>`)}</datalist>
      <small class="vp-editor-media-original" title=${selected.originalFilename}>Original: ${selected.originalFilename}</small>
      <div class="vp-editor-media-meta"><span>${formatBytes(selected.size)}</span><span>${formatDate(selected.createdAt)}</span></div>
      <div class="vp-editor-media-inspector-actions">
        <button type="button" class="is-primary" ?disabled=${isSaving || !draftName.trim()} @click=${() => void saveMetadata()}>
          <span class="icon-[material-symbols--check]"></span> Übernehmen
        </button>
        <button type="button" title="Medien-URL kopieren" aria-label="Medien-URL kopieren" @click=${async () => {
          try { await navigator.clipboard.writeText(new URL(selected.url, window.location.origin).href); setNotice('URL kopiert.'); }
          catch { setError('URL konnte nicht kopiert werden.'); }
        }}><span class="icon-[material-symbols--content-copy]"></span></button>
        <button
          type="button"
          class="is-danger ${deleteConfirmationId === selected.id ? 'is-confirming' : ''}"
          title=${selected.usageCount ? `Löschen nicht möglich: ${selected.usageCount} Verwendungen` : 'Medium löschen'}
          aria-label=${selected.usageCount
            ? `Medium wird ${selected.usageCount}-mal verwendet und kann nicht gelöscht werden`
            : deleteConfirmationId === selected.id ? 'Löschen endgültig bestätigen' : 'Medium löschen'}
          ?disabled=${isSaving || selected.usageCount > 0}
          @click=${() => void deleteAsset()}
        ><span class="icon-[material-symbols--delete]"></span>${deleteConfirmationId === selected.id ? html`<span>Bestätigen</span>` : ''}</button>
      </div>
      ${selected.usageCount ? html`<p class="vp-editor-media-usage"><span class="icon-[material-symbols--link]"></span> Dieses Medium ist ${selected.usageCount}-mal eingebunden und deshalb vor dem Löschen geschützt.</p>` : ''}
    </section>
  `;
});

const renderEditorMediaLibrary = (onInspect: () => void) => EditorMediaLibrary({ onInspect });
const renderEditorMediaInspector = () => EditorMediaInspector();

return { EditorMediaLibrary, EditorMediaInspector, renderEditorMediaLibrary, renderEditorMediaInspector };
};
